<template>
  <model-list-select
    :id="id"
    ref="selectSearch"
    v-model="field"
    v-validate="'required'"
    taggable
    class="select-search"
    label="value"
    :class="[
      { 'uppercase-all': uppercaseAllItems, 'icon-visible': iconVisible },
    ]"
    :placeholder="placeholder"
    :options="options"
    name="select"
    :filter="fuzzySort"
    @change="$emit('change', field)"
  >
    <template #option="option">
      <div
        v-if="option.label != null"
      >
        <component
          :is="part.highlighted ? 'strong' : 'span'"
          v-for="(part, index) in optionParts(option.label)"
          :key="index"
        >{{ part.text }}</component>
      </div>
      <div v-else-if="option.value != null">
        <em>{{ option.value }}</em>
      </div>
    </template>
    <span slot="no-options">
      <p
        v-if="noResultsMessageEnabled"
        class="v-select-no-matching"
      >
        {{ noResultsText }}
      </p>
    </span>
    <template
      v-if="footerEnabled"
      #list-footer
    >
      <div
        class="select-search__footer"
        @click="handleFooterAction()"
      >
        <p>{{ footerText }}</p>
      </div>
    </template>

    <template #spinner="{ loading }">
      <spinner v-if="loading" />
    </template>
  </model-list-select>
</template>

<script>
import fuzzysort from 'fuzzysort'
import Spinner from '@/components/common/Spinner'

export default {
  name: 'SelectSearch',
  components: {
    spinner: Spinner
  },
  props: {
    options: {
      type: Array,
      default: () => []
    },
    noResultsText: {
      type: String,
      default: 'Desculpe, não foram encontrados resultados para essa pesquisa.'
    },
    id: {
      type: String
    },
    placeholder: {
      default: 'Selecione...'
    },
    uppercaseAllItems: {
      default: false
    },
    footerEnabled: {
      default: false
    },
    footerText: {
      type: String
    },
    noResultsMessageEnabled: {
      default: true
    },
    spinnerEnabled: {
      default: false
    },
    isLoading: {
      default: false
    },
    iconVisible: {
      default: true
    }
  },
  data: () => {
    return {
      field: null
    }
  },
  watch: {
    field: function (newVal) {
      if (newVal?.value) {
        this.$emit('update:field', newVal.value)
      } else {
        this.$emit('update:field', newVal)
      }
    }
  },
  methods: {
    setLoading(value) {
      this.$refs.selectSearch.loading = value
    },
    handleFooterAction() {
      this.$emit('footer-action')
    },
    setNormalize(string) {
      return String(string ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    },
    fuzzySort(options, search) {
      const originalOptions = (options || []).filter(option => option != null)
      const searchNormalized = this.setNormalize(search)
      if (!searchNormalized) return originalOptions

      const searchableOptions = originalOptions.map(option => ({
        option,
        normalized: this.setNormalize(option.label ?? option.value ?? option.name ?? option)
      }))
      return fuzzysort.go(searchNormalized, searchableOptions, { key: 'normalized' })
        .map(result => result.obj.option)
    },
    optionParts(label) {
      const text = String(label ?? '')
      const search = this.setNormalize(this.$refs.selectSearch?.search)
      const match = search && fuzzysort.single(search, this.setNormalize(text))
      if (!match) return [{ text, highlighted: false }]

      const matchedIndexes = new Set(match.indexes)
      const parts = []
      let normalizedIndex = 0
      // Match indexes belong to normalized text. Keep the original spelling,
      // including combining accents, in the text nodes rendered by Vue.
      for (const character of text) {
        const normalizedLength = this.setNormalize(character).length
        const previous = parts[parts.length - 1]
        const highlighted = normalizedLength
          ? Array.from({ length: normalizedLength }, (_, index) => normalizedIndex + index)
            .some(index => matchedIndexes.has(index))
          : !!previous?.highlighted
        if (previous && previous.highlighted === highlighted) {
          previous.text += character
        } else {
          parts.push({ text: character, highlighted })
        }
        normalizedIndex += normalizedLength
      }
      return parts
    }
  }
}
</script>

<style lang="scss">
.select-search {
  width: 100%;

  &__footer {
    background: rgba(127, 26, 84, 0.05);
    padding: 1.3em 1.3em;
    color: #7f1a54;
    font-size: 14px;
    font-weight: 400;
    font-family: Roboto, sans-serif;
    cursor: pointer;

    &:hover {
      background-color: rgba(127, 26, 84, 0.09);
    }
  }

  .loader {
    border-top: 0.3em solid rgba(127, 26, 84, 0.2);
    border-right: 0.3em solid rgba(127, 26, 84, 0.2);
    border-bottom: 0.3em solid rgba(127, 26, 84, 0.2);
    border-left: 0.3em solid #7f1a54;
    width: 2em;
    height: 2em;
  }

  // Had to put important to overide component default styles
  input {
    text-transform: none !important;

    &::placeholder {
      font-weight: 400;
      font-size: 14px;
      color: #7a7a7a !important;
      padding-left: 0px !important;
    }
  }

  .highlight,
  .dropdown-menu li:hover {
    background: #f7f7f7;
    color: #4a4a4a;
  }

  &.icon-visible {
    .vs__selected-options {
      padding-left: 1.8em;
      background-image: url("/static/images/icon-search-light-gray.svg");
      background-position-x: 0;
      background-position-y: 6px;
      background-size: 20px;
      background-repeat: no-repeat;
    }
  }

  &.uppercase-all {

    & input,
    & .vs__selected,
    & .dropdown-toggle,
    & .dropdown-menu {
      text-transform: uppercase !important;
    }

    & .search::placeholder {
      text-transform: none !important;
    }
  }

  .dropdown-menu {
    padding-bottom: 0;

    li {
      padding: 0.6em 1.3em !important;
      font-size: 14px;
      color: #4a4a4a;
    }
  }
}
</style>
